import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Group,
  Loader,
  Paper,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconHeadphones, IconPhone, IconPlus, IconSearch } from '@tabler/icons-react';
import { isAxiosError } from 'axios';
import dayjs from 'dayjs';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState } from '../../components/EmptyState';
import { apiClient } from '../../api/client';
import type { CustomerDto } from '../customers/types';
import { useOrdersByCustomer } from './callCenterApi';
import { PhoneOrderDrawer } from './PhoneOrderDrawer';

// Call Center / phone-order page. Agent types a phone, server resolves
// the customer (or returns 404), agent sees recent history and a
// "new order" button. The actual order entry lives in PhoneOrderDrawer
// to keep this page focused on lookup + history.

export function CallCenterPage() {
  const { t } = useTranslation();
  const [phone, setPhone] = useState('');
  const [customer, setCustomer] = useState<CustomerDto | null>(null);
  const [looking, setLooking] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const ordersQuery = useOrdersByCustomer(customer?.customerId ?? null);

  const lookup = async () => {
    const trimmed = phone.trim();
    if (!trimmed) return;
    setLooking(true);
    try {
      const resp = await apiClient.get<CustomerDto>(
        `/api/customers/lookup?phone=${encodeURIComponent(trimmed)}`,
      );
      setCustomer(resp.data);
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 404) {
        notifications.show({
          color: 'orange',
          title: t('callCenter.lookup.notFoundTitle'),
          message: t('callCenter.lookup.notFoundBody'),
        });
        setCustomer(null);
      } else {
        notifications.show({
          color: 'red',
          title: t('common.toasts.errorTitle'),
          message: err instanceof Error ? err.message : t('common.toasts.errorBody'),
        });
      }
    } finally {
      setLooking(false);
    }
  };

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('nav.sections.operations')}
        title={t('callCenter.page.title')}
        subtitle={t('callCenter.page.subtitle')}
      />

      <Paper p="md">
        <Group gap="sm" wrap="nowrap" align="flex-end">
          <TextInput
            label={t('callCenter.lookup.phoneLabel')}
            description={t('callCenter.lookup.phoneHelp')}
            placeholder="0599123456"
            value={phone}
            onChange={(e) => setPhone(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void lookup();
            }}
            leftSection={<IconPhone size={16} stroke={1.6} />}
            style={{ flex: 1 }}
            dir="ltr"
            className="font-numeric"
            size="md"
          />
          <Button
            size="md"
            onClick={lookup}
            loading={looking}
            disabled={phone.trim().length === 0}
            leftSection={<IconSearch size={16} stroke={1.6} />}
          >
            {t('callCenter.lookup.lookupCta')}
          </Button>
        </Group>
      </Paper>

      {customer ? (
        <Stack gap="md">
          <Paper p="md">
            <Group justify="space-between" align="flex-end" wrap="nowrap">
              <div>
                <Text fw={600} size="lg" c="var(--wheels-ink-strong)">
                  {customer.fullName}
                </Text>
                <Group gap="xs">
                  <Text size="sm" c="dimmed" dir="ltr" className="font-mono">
                    {customer.phone}
                  </Text>
                  {customer.email && (
                    <Text size="sm" c="dimmed" dir="ltr">
                      · {customer.email}
                    </Text>
                  )}
                </Group>
                <Group gap="xs" mt={4}>
                  <Badge size="sm" variant="light" color="wheels">
                    {t('callCenter.customer.pointsBalance', {
                      balance: customer.pointsBalance,
                    })}
                  </Badge>
                  {!customer.isActive && (
                    <Badge size="sm" variant="light" color="gray">
                      {t('common.inactive')}
                    </Badge>
                  )}
                </Group>
              </div>
              <Button
                leftSection={<IconPlus size={16} stroke={1.8} />}
                onClick={() => setDrawerOpen(true)}
                disabled={!customer.isActive}
              >
                {t('callCenter.order.newOrderCta')}
              </Button>
            </Group>
          </Paper>

          <Paper p={ordersQuery.data && ordersQuery.data.length > 0 ? 0 : 'lg'}>
            {ordersQuery.isLoading ? (
              <Stack align="center" py={32}>
                <Loader size="sm" />
              </Stack>
            ) : !ordersQuery.data || ordersQuery.data.length === 0 ? (
              <EmptyState
                icon={<IconHeadphones size={22} stroke={1.6} />}
                title={t('callCenter.history.emptyTitle')}
                description={t('callCenter.history.emptyBody')}
              />
            ) : (
              <Table verticalSpacing="sm" highlightOnHover striped="even">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t('callCenter.history.columns.number')}</Table.Th>
                    <Table.Th>{t('callCenter.history.columns.type')}</Table.Th>
                    <Table.Th>{t('callCenter.history.columns.total')}</Table.Th>
                    <Table.Th>{t('callCenter.history.columns.opened')}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {ordersQuery.data.map((o) => (
                    <Table.Tr key={o.orderId}>
                      <Table.Td className="font-mono">{o.displayNumber}</Table.Td>
                      <Table.Td>{renderType(o.orderType, t)}</Table.Td>
                      <Table.Td className="font-numeric">
                        {o.total.toFixed(2)}
                      </Table.Td>
                      <Table.Td c="var(--wheels-ink-muted)" fz="xs">
                        {dayjs(o.openedAt).format('YYYY-MM-DD HH:mm')}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </Paper>
        </Stack>
      ) : (
        <Paper p="lg">
          <EmptyState
            icon={<IconHeadphones size={22} stroke={1.6} />}
            title={t('callCenter.empty.title')}
            description={t('callCenter.empty.body')}
          />
        </Paper>
      )}

      {customer && (
        <PhoneOrderDrawer
          opened={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          customer={customer}
        />
      )}
    </Stack>
  );
}

function renderType(type: number, t: (k: string) => string): string {
  switch (type) {
    case 1:
      return t('callCenter.history.typeDineIn');
    case 2:
      return t('callCenter.history.typeTakeaway');
    case 3:
      return t('callCenter.history.typeDelivery');
    default:
      return '—';
  }
}
